import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NavigationContext = createContext();

// Helper function to map URL paths to views (defined outside component for stability)
const getViewFromPath = (path) => {
  const pathMap = {
    '/': 'home',
    '/login': 'login',
    '/signup': 'signup',
    '/reset-password': 'resetPassword',
    '/home': 'home',
    '/dashboard': 'home', // Keep for backward compatibility
    '/stock-onboarding': 'stockOnboarding',
    '/manage-stock': 'manageStock',
    '/manage-stock/upload-invoice': 'uploadInvoice',
    '/manage-stock/indent-estimate': 'indentEstimate',
    '/manage-stock/add-store': 'addStore',
    '/manage-stock/shift-transfer': 'shiftTransfer',
    '/manage-stock/stock-received': 'stockReceived',
    '/manage-stock/accessories': 'accessories',
    '/sheets': 'sheets',
    '/sheets/update-closing-stock': 'updateClosingStock',
    '/sheets/income-expenses': 'incomeExpenses',
    '/sheets/track-payments': 'trackPayments',
    '/reports': 'reports',
    '/reports/income-expenses-report': 'incomeExpensesReport',
    '/reports/download-sale-sheet': 'downloadSaleSheet',
    '/reports/stock-lifted': 'stockLifted',
    '/reports/sales-report': 'brandWiseSales',
    '/reports/stock-transfer-report': 'stockTransferReport'
  };
  return pathMap[path] || null;
};

// Helper function to map views to URL paths (defined outside component for stability)
const getPathFromView = (view) => {
  const viewMap = {
    'login': '/login',
    'signup': '/signup',
    'resetPassword': '/reset-password',
    'home': '/home',
    'stockOnboarding': '/stock-onboarding',
    'manageStock': '/manage-stock',
    'uploadInvoice': '/manage-stock/upload-invoice',
    'indentEstimate': '/manage-stock/indent-estimate',
    'addStore': '/manage-stock/add-store',
    'shiftTransfer': '/manage-stock/shift-transfer',
    'stockReceived': '/manage-stock/stock-received',
    'accessories': '/manage-stock/accessories',
    'sheets': '/sheets',
    'updateClosingStock': '/sheets/update-closing-stock',
    'incomeExpenses': '/sheets/income-expenses',
    'trackPayments': '/sheets/track-payments',
    'reports': '/reports',
    'incomeExpensesReport': '/reports/income-expenses-report',
    'downloadSaleSheet': '/reports/download-sale-sheet',
    'stockLifted': '/reports/stock-lifted',
    'brandWiseSales': '/reports/sales-report',
    'stockTransferReport': '/reports/stock-transfer-report'
  };
  return viewMap[view] || '/home';
};

// Initialize view from URL
const getInitialView = () => {
  const path = window.location.pathname;
  const viewFromPath = getViewFromPath(path);
  return viewFromPath || 'login';
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children, onLogout }) => {

  const [currentView, setCurrentView] = useState(getInitialView);

  const handleNavigate = useCallback((view) => {
    const path = getPathFromView(view);
    window.history.pushState({ view }, '', path);
    setCurrentView(view);
  }, []); // No dependencies needed - functions are stable

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      const path = window.location.pathname;
      const viewFromPath = getViewFromPath(path);
      if (viewFromPath) {
        setCurrentView(viewFromPath);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // No dependencies needed - functions are stable

  const value = {
    currentView,
    navigate: handleNavigate,
    logout: onLogout
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

